import {
    FunctionToolCallResponseItemDto,
    McpCallResponseItemDto,
    ResponsesMessageRecord
} from '../../api/responses/responses-types';
import parseDynamicSessionOutput from '../../lib/parseDynamicSessionOutput';
import {isBrowserActionToolName} from '../../lib/browserActionTools';
import {getWebSearchOutputStatus} from './webSearchOutputStatus';

/**
 * Decides what the thread shows at the `simple` detail level (see
 * ToolSettingsContext). Standard/Advanced render every item as they always have, so
 * nothing here is consulted for them.
 *
 * Applied at render time only. The message list must keep operating on the full
 * record set: handled-approval tracking and form-submission blocking are derived
 * from records (mcp_call, mcp_approval_response) that this file hides.
 */

export type FunctionCallKind =
    | 'background_job'
    | 'show_form'
    | 'save_to_file'
    | 'search_assistant_files'
    | 'fetch_assistant_files'
    | 'web_search'
    | 'dynamic_session'
    | 'browser_action'
    | 'agentic_session'
    | 'ask_assistant'
    | 'generic';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ParsedFunctionCallOutput = any;

export const parseFunctionCallOutput = (
    functionCall: FunctionToolCallResponseItemDto
): ParsedFunctionCallOutput => {
    if (!functionCall.output) return null;
    try {
        return JSON.parse(functionCall.output);
    } catch {
        return null;
    }
};

/**
 * Single source of truth for which renderer a function call maps to — used by
 * FunctionCallOutput's dispatch and by the simple-view rules below.
 */
export const classifyFunctionCall = (
    functionCall: FunctionToolCallResponseItemDto,
    parsedOutput: ParsedFunctionCallOutput
): FunctionCallKind => {
    if (
        parsedOutput?.type === 'background_job' &&
        parsedOutput.job_id &&
        parsedOutput.renderer
    ) {
        return 'background_job';
    }
    if (
        functionCall.name === 'show_form' &&
        parsedOutput?.type === 'assistant_form' &&
        parsedOutput?.interactionMode === 'attended' &&
        parsedOutput.formName
    ) {
        return 'show_form';
    }
    if (
        functionCall.name === 'save_to_file' &&
        parsedOutput?.purpose === 'template_output'
    ) {
        return 'save_to_file';
    }
    if (functionCall.name === 'search_assistant_files') {
        return 'search_assistant_files';
    }
    // Knowledge-file fetch tools (TA-741). Trace, not a user-facing result: hidden at
    // the simple level like search (isVisibleInSimpleView's default branch).
    if (
        functionCall.name === 'read_assistant_file' ||
        functionCall.name === 'list_assistant_files'
    ) {
        return 'fetch_assistant_files';
    }
    if (functionCall.name === 'web_search') {
        return 'web_search';
    }
    if (functionCall.name?.startsWith('dynamic_session_')) {
        return 'dynamic_session';
    }
    // Chrome-extension browser actions (snapshot/act/navigate/tabs/screenshot):
    // a trace like the other tool calls, hidden at the simple level unless it failed.
    if (isBrowserActionToolName(functionCall.name)) {
        return 'browser_action';
    }
    // Agentic mode: the approval card (enter) and the session summary (exit). The tool's
    // refusals carry an error and fall through to generic trace.
    if (
        (functionCall.name === 'enter_agentic_mode' ||
            functionCall.name === 'exit_agentic_mode') &&
        parsedOutput?.type === 'agentic_session' &&
        !parsedOutput.error
    ) {
        return 'agentic_session';
    }
    if (functionCall.name === 'ask_assistant') {
        return 'ask_assistant';
    }
    return 'generic';
};

/**
 * True when the MCP error carries something displayable. OpenAI's spec says a string,
 * but in practice it may be an object — an empty object is not an error.
 */
export const mcpCallHasError = (mcpCall: McpCallResponseItemDto): boolean => {
    const {error} = mcpCall;
    if (error === null || error === undefined) return false;
    if (typeof error === 'string') return error.length > 0;
    if (typeof error === 'object') return Object.keys(error).length > 0;
    return true;
};

const parseMcpOutput = (mcpCall: McpCallResponseItemDto): unknown => {
    if (!mcpCall.output) return null;
    try {
        return JSON.parse(mcpCall.output);
    } catch {
        return null;
    }
};

/** The M365 drive picker is an interactive result the user works with, not a trace. */
export const isM365DrivePickerCall = (
    mcpCall: McpCallResponseItemDto
): boolean => {
    if (mcpCallHasError(mcpCall)) return false;
    if (mcpCall.name === 'ta_m365_show_drive_picker') return true;
    const output = parseMcpOutput(mcpCall);
    return (
        !!output &&
        typeof output === 'object' &&
        (output as Record<string, unknown>).type === 'm365_drive_picker'
    );
};

const functionCallHasError = (
    functionCall: FunctionToolCallResponseItemDto
): boolean => {
    if (functionCall.status === 'failed') return true;

    const parsedOutput = parseFunctionCallOutput(functionCall);
    switch (classifyFunctionCall(functionCall, parsedOutput)) {
        case 'dynamic_session': {
            // Mirrors DynamicSessionOutput's isFailed for the code tools.
            const isCodeTool =
                functionCall.name === 'dynamic_session_run_python' ||
                functionCall.name === 'dynamic_session_run_shell';
            if (!isCodeTool) return false;
            const status = parseDynamicSessionOutput(
                functionCall.output
            ).status;
            return !!status && !/^succe/i.test(status.trim());
        }
        case 'web_search': {
            // Success is result XML; failures come back as plain text.
            const output = functionCall.output?.trim() ?? '';
            if (
                !output ||
                output.startsWith('<') ||
                output.startsWith('```xml')
            ) {
                return false;
            }
            return getWebSearchOutputStatus(output).kind === 'error';
        }
        case 'browser_action':
            // Tool output is {ok, action, result} or {ok:false, status, error}.
            return parsedOutput?.ok === false;
        case 'ask_assistant':
            // {status: completed|needs_approval|timeout|failed|not_found|ambiguous}.
            return (
                !!parsedOutput?.status &&
                parsedOutput.status !== 'completed' &&
                parsedOutput.status !== 'needs_approval'
            );
        default:
            return false;
    }
};

/** Errors are never hidden, whatever the detail level. */
export const threadItemHasError = (record: ResponsesMessageRecord): boolean => {
    if (record.metadata?.dynamic_session_activity === 'true') {
        return record.metadata.dynamic_session_activity_level === 'error';
    }
    switch (record.messageType) {
        case 'mcp_call':
            return mcpCallHasError(record.message as McpCallResponseItemDto);
        case 'function_call':
            return functionCallHasError(
                record.message as FunctionToolCallResponseItemDto
            );
        case 'code_interpreter_call':
        case 'shell_call':
            return (record.message as {status?: string}).status === 'failed';
        default:
            return false;
    }
};

export interface SimpleViewContext {
    /** For mcp_approval_request: whether a matching mcp_call already answered it. */
    isApprovalHandled?: boolean;
}

/**
 * Whether a record is shown at the `simple` level: the conversation itself, plus
 * errors, pending approvals, interactive results and user-facing outputs.
 */
export const isVisibleInSimpleView = (
    record: ResponsesMessageRecord,
    {isApprovalHandled = false}: SimpleViewContext = {}
): boolean => {
    if (threadItemHasError(record)) return true;

    // Agentic-session notes (stopped, interrupted, declined, ended) are for the person.
    if (record.metadata?.agentic_session_event) return true;

    // Runtime progress lines ("Read skill instructions.", "Ran skill code.") are
    // persisted as assistant messages; only their error-level ones survive (above).
    if (record.metadata?.dynamic_session_activity === 'true') return false;

    switch (record.messageType) {
        case 'user_message':
            // A browser-interaction result posted back by the extension is trace.
            return !record.metadata?.browser_result_call_id;
        case 'assistant_message':
        case 'system_message':
        case 'developer_message':
        case 'unknown_or_unsupported':
            return true;
        case 'mcp_approval_request':
            // The run is blocked until the user answers — always show while pending.
            return !isApprovalHandled;
        case 'mcp_call':
            return isM365DrivePickerCall(
                record.message as McpCallResponseItemDto
            );
        case 'function_call': {
            const functionCall =
                record.message as FunctionToolCallResponseItemDto;
            const kind = classifyFunctionCall(
                functionCall,
                parseFunctionCallOutput(functionCall)
            );
            return (
                kind === 'background_job' ||
                kind === 'show_form' ||
                kind === 'save_to_file' ||
                // The approval card and the session summary are the person's business.
                kind === 'agentic_session'
            );
        }
        case 'compaction':
            // The "conversation compacted" marker is thread furniture, not conversation.
            return false;
        default:
            // reasoning, mcp_list_tools, search/code/shell calls, approval responses…
            return false;
    }
};
